/**
 * Descuentos por producto y a la cuenta: helpers compartidos entre la mesa,
 * la precuenta, el cobro, la división y el detalle de la orden.
 *
 * Un descuento con `orderItemId` es de ese renglón (cortesía o descuento a un
 * producto); sin él, es de la cuenta completa. El monto que manda el backend en
 * una orden abierta ya es el vigente, así que las sumas de aquí cuadran con lo
 * que se va a cobrar.
 */
import type { OrderDiscount, OrderItem } from '../types/orders';

type DiscountLike = Pick<OrderDiscount, 'reason' | 'discountAmount'> &
  Partial<Pick<OrderDiscount, 'id' | 'orderItemId' | 'productName' | 'type' | 'value'>>;

type ItemLike = Pick<OrderItem, 'id' | 'productName'> & Partial<Pick<OrderItem, 'dailyMenuName'>>;

export const isItemDiscount = (d: Pick<OrderDiscount, 'orderItemId'>) => !!d.orderItemId;

/** 100% de un producto: la cortesía de siempre. */
export const isCourtesy = (d: Partial<Pick<OrderDiscount, 'orderItemId' | 'type' | 'value'>>) =>
  !!d.orderItemId && d.type === 'percentage' && Number(d.value) >= 100;

/** Nombre del producto al que apunta el descuento (del backend o de los renglones a mano). */
export function discountProductName(d: DiscountLike, items?: ItemLike[]): string | undefined {
  if (!d.orderItemId) return undefined;
  if (d.productName) return d.productName;
  const item = items?.find(i => i.id === d.orderItemId);
  return item ? item.dailyMenuName || item.productName : undefined;
}

/**
 * Lo que dice el ticket y la pantalla: "Cortesía (Café americano)". El de la
 * cuenta completa se queda con su motivo tal cual.
 */
export function discountLabel(d: DiscountLike, items?: ItemLike[]): string {
  const reason = d.reason || 'Descuento';
  const product = discountProductName(d, items);
  return product ? `${reason} (${product})` : reason;
}

/**
 * Copias con `reason` ya etiquetado. Los servicios de impresión (Cloud Print,
 * Print Agent, Bluetooth) leen `reason`, así que etiquetar una vez aquí basta
 * para que el ticket diga a qué producto fue la cortesía.
 */
export function withDiscountLabels<T extends DiscountLike>(discounts: T[], items?: ItemLike[]): T[] {
  return discounts.map(d => (d.orderItemId ? { ...d, reason: discountLabel(d, items) } : d));
}

/** Descuento activo de cada renglón (el backend admite uno por renglón). */
export function itemDiscountsById<T extends DiscountLike>(discounts: T[] | undefined): Record<string, T> {
  const byId: Record<string, T> = {};
  for (const d of discounts || []) {
    if (d.orderItemId && !byId[d.orderItemId]) byId[d.orderItemId] = d;
  }
  return byId;
}

/** Importe del renglón después de su descuento de producto. */
export function netItemTotal(item: Pick<OrderItem, 'id' | 'total'>, byId: Record<string, DiscountLike>): number {
  const discount = byId[item.id]?.discountAmount ?? 0;
  return Math.max(0, item.total - discount);
}

/**
 * Etiqueta corta para el renglón: "Cortesía", "Cortesía ×1" (una pieza de
 * varias), "-20%" o "-$15.00".
 */
export function itemDiscountTag(
  d: DiscountLike,
  item: Pick<OrderItem, 'quantity' | 'total'>
): string {
  if (isCourtesy(d)) return 'Cortesía';
  if (d.type === 'percentage') return `-${Number(d.value)}%`;
  const unit = item.quantity > 0 ? item.total / item.quantity : 0;
  if (unit > 0) {
    const pieces = d.discountAmount / unit;
    const whole = Math.round(pieces);
    if (whole >= 1 && Math.abs(pieces - whole) < 0.005) {
      return whole >= item.quantity ? 'Cortesía' : `Cortesía ×${whole}`;
    }
  }
  return `-$${d.discountAmount.toFixed(2)}`;
}

/**
 * Redondeo a centavos. Restar descuentos en punto flotante deja residuos
 * (35.90 + 42.50 + 18.70 todo en cortesía da 1.4e-14, no 0): cualquier total que
 * se compare contra cero tiene que pasar por aquí.
 */
export const roundCents = (n: number) => Math.round(n * 100) / 100;

/** Monto de N piezas de un renglón, al centavo (cortesía parcial). */
export const piecesAmount = (item: Pick<OrderItem, 'quantity' | 'total'>, pieces: number) =>
  Math.round(((item.total * pieces) / item.quantity) * 100) / 100;
